import React from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';

export interface RendererOption {
  label: string;       // displayName
  value: string;       // internal name (sent to backend)
  description?: string;
}

interface RendererPickerModalProps {
  show: boolean;
  title?: string;
  options: RendererOption[];
  onClose: () => void;                           // called on Cancel or backdrop click
  onPick: (rendererName: string | null) => void; // null = "use service default"
}

interface RendererPickerModalState {
  selected: RendererOption | null;
}

// Fully-controlled, portal-rendered modal. Sidesteps Bootstrap's
// modal-stacking issues — when launched from inside another modal
// (e.g. SamplesModal), Bootstrap doesn't bump nested backdrops above
// the parent's z-index, so the popup ends up visually-buried. This
// component renders its own backdrop + content via React portal into
// document.body and uses an explicit z-index above Bootstrap's
// defaults (modal=1050, backdrop=1040).
export class RendererPickerModal extends React.Component<RendererPickerModalProps, RendererPickerModalState> {
  constructor(props: RendererPickerModalProps) {
    super(props);
    this.state = { selected: null };
  }

  // Reset selection whenever the modal closes so reopening doesn't
  // show a stale choice.
  componentDidUpdate(prev: RendererPickerModalProps) {
    if (prev.show && !this.props.show) {
      this.setState({ selected: null });
    }
  }

  render() {
    if (!this.props.show) return null;
    const { title, options, onClose, onPick } = this.props;

    // Very high z-index so the picker sits above whichever modal
    // launched it. The project (or Bootstrap's modal-open behavior)
    // can push parent modals well above z=1050, so we pick a value
    // that's clearly above any reasonable stacking context.
    const backdropStyle: React.CSSProperties = {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 99998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
    const dialogStyle: React.CSSProperties = {
      background: '#fff', borderRadius: 6, width: '90%', maxWidth: 500,
      boxShadow: '0 10px 40px rgba(0,0,0,0.25)', zIndex: 99999,
      display: 'flex', flexDirection: 'column',
    };

    const modal = (
      <div
        style={backdropStyle}
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          // Close if backdrop (not dialog content) is clicked
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h5 className="modal-title">{title || 'Generate Report'}</h5>
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <label style={{ fontWeight: 600, marginBottom: 6 }}>Render as</label>
            <Select
              isClearable={true}
              isSearchable={true}
              placeholder="(use service default)"
              value={this.state.selected}
              onChange={(e) => this.setState({ selected: e as RendererOption | null })}
              options={options}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 100000 }) }}
            />
            <small className="form-text text-muted mt-2">
              Leave blank to use the service's default renderer. Otherwise pick
              a specific version of the report to generate for this sample.
            </small>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                const chosen = this.state.selected ? this.state.selected.value : null;
                onPick(chosen);
              }}
            >
              Download
            </button>
          </div>
        </div>
      </div>
    );
    return ReactDOM.createPortal(modal, document.body);
  }
}
